
import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/lib/logger";
import { WebsiteAnalysisResult, AnalysisErrorType } from "./types";
import * as Sentry from "@sentry/react";
import { handleEdgeFunctionError } from "./utils/errorHandlers";

/**
 * Analyze website using the edge function
 * @param url The website URL to analyze
 * @returns Analysis results
 */
export async function analyzeWithEdgeFunction(url: string): Promise<WebsiteAnalysisResult> {
  const transaction = Sentry.startTransaction({
    name: "website-analysis-edge-function",
    op: "function"
  });

  try {
    Sentry.configureScope(scope => {
      scope.setTag("analysis_method", "edge_function");
      scope.setContext("analysis_details", { url });
    });

    logger.info({
      message: "Starting website analysis via edge function",
      context: { url, method: "edge_function" }
    });

    // Create a promise that rejects after the specified timeout
    const timeout = new Promise<never>((_, reject) => {
      const timeoutId = setTimeout(() => {
        reject(new Error("Edge function timeout after 80 seconds"));
      }, 80000); // 80 seconds timeout
      
      // Store the timeout ID in a way that it can be cleared if the main promise resolves
      // @ts-ignore - Adding custom property to the promise
      timeout.timeoutId = timeoutId;
    });
    
    // Create the main promise for the function call
    const functionCall = supabase.functions.invoke('analyze-website', {
      body: { url },
      headers: { prefer: 'return=minimal' }
    });
    
    // Race between the timeout and the actual function call
    const result = await Promise.race([functionCall, timeout])
      .finally(() => {
        // Clear the timeout to prevent memory leaks
        // @ts-ignore - Accessing custom property from the promise
        clearTimeout(timeout.timeoutId);
      });
    
    // Since we're using Promise.race, result will be the functionCall result if it completed in time
    const { data, error } = result;
    
    if (error) {
      logger.error({
        message: "Edge function error",
        error,
        context: { url }
      });
      throw new Error(error.message || "Supabase function error");
    }
    
    if (!data) {
      logger.error({
        message: "No data returned from analysis function",
        context: { url }
      });
      throw new Error("No data returned from analysis function");
    }
    
    logger.info({
      message: "Website analysis completed successfully via edge function",
      context: { 
        url, 
        dataReceived: !!data,
        fieldCount: Object.keys(data).length
      }
    });

    transaction.finish();
    return data;
  } catch (error) {
    handleEdgeFunctionError(error, url);
    transaction.finish();
    throw error;
  }
}
